import { none, some } from '@sapphire/result';
import {
  SurveyStateFilters,
  SurveyStatePagination,
  SurveyRequestContext,
  SurveyState,
} from '../survey-state.model';
import { Survey, SurveySort } from '../../models/survey.model';
import {
  INITIAL_SURVEY_FILTERS,
  INITIAL_SURVEY_PAGINATION,
} from '../survey.state';

// ---------------------------------------------------------------------------
// SurveyStateBuilder
//
// Fluent builder for producing a new SurveyState.
// Always operates on a shallow copy — the original NgRx state is never mutated.
// Obtain an instance via the static factory: SurveyStateBuilder.copy(state)
// Finalise the build with .build() to get the resulting SurveyState.
// ---------------------------------------------------------------------------

export class SurveyStateBuilder {
  private constructor(private readonly _draft: SurveyState) {}

  public static copy(state: SurveyState): SurveyStateBuilder {
    return new SurveyStateBuilder({ ...state });
  }

  /** Empties the survey list without touching pagination or filters. */
  public clearSurveys(): SurveyStateBuilder {
    this._draft.surveys = none;
    return this;
  }

  /** Removes any active error message. */
  public clearError(): SurveyStateBuilder {
    this._draft.error = null;
    return this;
  }

  /** Removes the active sort (API will use its default ordering). */
  public clearSort(): SurveyStateBuilder {
    this._draft.sort = null;
    return this;
  }

  /**
   * Resets only the page cursor and hasMore flag back to initial values.
   * Intentionally preserves `limit` — it is a user UI preference, not a filter,
   * so it should survive filter/sort/context changes.
   */
  public resetPage(): SurveyStateBuilder {
    this._draft.pagination = {
      ...this._draft.pagination,
      page: INITIAL_SURVEY_PAGINATION.page,
      hasMore: INITIAL_SURVEY_PAGINATION.hasMore,
    };
    return this;
  }

  /** Resets filters to their initial defaults. */
  public resetFilters(): SurveyStateBuilder {
    this._draft.filters = { ...INITIAL_SURVEY_FILTERS };
    return this;
  }

  // -- Setters --------------------------------------------------------------

  /** Merges partial filter overrides into the current filter set. */
  public setFilters(filters: Partial<SurveyStateFilters>): SurveyStateBuilder {
    this._draft.filters = { ...this._draft.filters, ...filters };
    return this;
  }

  /** Replaces the active sort entirely. */
  public setSort(sort: SurveySort): SurveyStateBuilder {
    this._draft.sort = { ...sort };
    return this;
  }

  /** Replaces the request context (buyer/UI type). */
  public setContext(context: SurveyRequestContext): SurveyStateBuilder {
    this._draft.context = { ...context };
    return this;
  }

  /**
   * Appends incoming surveys to existing ones (infinite scroll).
   * When the list was empty (none), initialises it with the incoming surveys.
   */
  public setSurveys(surveys: Survey[]): SurveyStateBuilder {
    this._draft.surveys = this._draft.surveys.match({
      some: (prev) => some([...prev, ...surveys]),
      none: () => some(surveys),
    });
    return this;
  }

  /** Applies a partial patch to the current pagination object. */
  public setPagination(
    patch: Partial<SurveyStatePagination>,
  ): SurveyStateBuilder {
    this._draft.pagination = { ...this._draft.pagination, ...patch };
    return this;
  }

  public setLoading(loading: boolean): SurveyStateBuilder {
    this._draft.loading = loading;
    return this;
  }

  public setError(error: string): SurveyStateBuilder {
    this._draft.error = error;
    return this;
  }

  /** Returns the immutable state snapshot produced by this builder chain. */
  public build(): SurveyState {
    return this._draft;
  }
}
